import * as React from 'react';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { Textbox } from '../textbox/textbox';
import { PrimaryButton } from '../../buttons/primary-button';
import { Autocomplete } from '../search-bar/autocomplete';
import { IAutocompleteResults } from '../../../state-helpers/autocomplete';
import { setActiveHit, setAutocompleteActive, setAutocompleteHits } from '../../../actions/autocomplete-actions';
import { bindActionCreators } from 'redux';

/// <summary>
/// This is a controlled component used to render a search bar
/// To use autocomplete, pass in an autocompleteModel that implements IAutocompleteResults
/// And call setAutocompleteResults with the string array of hits based on search term inside the onChange handler
/// </summary>
/// <param name="dispatch">The Redux dispatch function</param>
/// <param name="searchValue">The controlled value of the search term</param>
/// <param name="onSearch">Callback function when the user submits the search</param>
/// <param name="onChange">Callback function when the text is changed. Use setAutocompleteResults as explained above.</param>
/// <param name="id">A unique string identifier used for HTML.</param>
/// <param name="autocompleteModel">The autocomplete model (optional, default=null)</param>
/// <param name="charsForAutocomplete">Number of characters before autocomplete displays (optional, default=1)</param>
/// <param name="searchButtonText">Text for the search button (optional, default='Search')</param>
/// <param name="defaultValue">Default value to pre-populate the textbox (optional, default='')</param>
/// <param name="placeHolder">Default placeholder text (optional, default='')</param>
/// <param name="customClasses">Used to supply any additional classes to the surrounding div (optional, default='')</param>
interface IStateProps {

}

interface IDispatchProps {
    setActiveHit: (index: number) => void;
    setAutocompleteHits: (hits: string[]) => void;
    setAutocompleteActive: (active: boolean) => void;
}

interface IOwnProps {
    id: string;
    searchValue: string;
    onSearch: (val: string) => void;
    onChange: (val: string, setAutocompleteResults?: (hits: string[]) => void) => void;
    autocompleteModel?: IAutocompleteResults;
    charsForAutocomplete?: number;
    searchButtonText?: string;
    defaultValue?: string;
    placeHolder?: string;
    customClasses?: string;
    searchBoxText?: string;
    setOpen?: boolean;
}

type IProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    searchBarOpen: boolean;
}

export class SearchBar extends React.Component<IProps, IState> {
    static defaultProps = {
        autocompleteModel: null,
        charsForAutocomplete: 1,
        searchButtonText: "Search",
        defaultValue: "",
        placeHolder: "",
        customClasses: "",
    }

    constructor(props) {
        super(props);
        this.HasAutocomplete = this.props.autocompleteModel != null;
        this.TextboxId = this.props.id + "-searchtextbox";
        let isMobile = window.innerWidth < 600;

        this.state = {
            searchBarOpen: isMobile || this.props.setOpen ? true : false,
        }
    }

    HasAutocomplete: boolean;
    TextboxId: string;

    KeyDownHandler(e) {
        // If no autocomplete
        if (!this.HasAutocomplete) {
            if (e.keyCode == 13) {
                this.props.onSearch(this.props.searchValue);
            }
            return;
        }

        switch (e.keyCode) {
            //Up arrow
            //Move selected autocomplete result up one
            case 38: {
                if (this.props.autocompleteModel.activeHit == -1)
                    this.props.setActiveHit(this.props.autocompleteModel.hits.length - 1);
                else
                    this.props.setActiveHit(this.props.autocompleteModel.activeHit - 1);

                e.preventDefault();

                break;
            }
            //Down arrow
            //Move selected autocomplete result down one
            case 40: {
                if (this.props.autocompleteModel.activeHit == this.props.autocompleteModel.hits.length - 1)
                    this.props.setActiveHit(-1);
                else
                    this.props.setActiveHit(this.props.autocompleteModel.activeHit + 1);
                break;
            }
            //Enter key
            //If an autocomplete option is selected, change the search text to that, otherwise submit
            case 13: {
                var term = this.props.searchValue;

                if (this.props.autocompleteModel.activeHit > -1) {
                    term = this.props.autocompleteModel.hits[this.props.autocompleteModel.activeHit];
                    this.props.setActiveHit(-1);
                }

                this.props.onSearch(term);
                this.ForceTextUpdate(term);
                this.props.setActiveHit(-1);
                this.props.setAutocompleteHits([]);
            }
        }
    }
    get IsMobile() {
        return window.innerWidth < 961;
    }

    get ButtonDisplayClass() {
        // mobile responsiveness handled in global-search-bar.scss
        return (this.props.searchBoxText && !this.IsMobile) ? "show-box-text" : "show-button-text";
    }

    ForceTextUpdate(newVal: string) {
        (document.getElementById(this.TextboxId) as HTMLInputElement).value = newVal;
    }

    UpdateSearchText(e) {
        if (this.HasAutocomplete) {
            let showAutocomplete = (e.currentTarget.value.length >= this.props.charsForAutocomplete);
            this.props.onChange(e.currentTarget.value, (hits) => {
                this.props.setAutocompleteHits(hits);
                this.props.setAutocompleteActive(showAutocomplete);
            });
        } else {
            this.props.onChange(e.currentTarget.value);
        }
    }

    AutocompleteResultClicked(value) {
        this.props.onSearch(value);
        this.ForceTextUpdate(value);
        this.props.setActiveHit(-1);
        this.props.setAutocompleteHits([]);
    }

    onBlurHandler(e: any) {
        if (this.props.autocompleteModel.activeHit == -1)
            this.props.setAutocompleteActive(false);
    }

    onFocusHandler(e: any) {
        if (this.props.searchValue.length >= this.props.charsForAutocomplete) {
            this.props.setAutocompleteActive(true);

        }
    }

    searchClear(e) {
        this.props.onChange('', (hits) => { });
        this.props.setActiveHit(-1);
        this.props.setAutocompleteHits([]);
        this.ForceTextUpdate('');
        (document.getElementById(this.TextboxId) as HTMLInputElement).focus();
    }

    handleSearchButtonClick = () => {

        if (this.state.searchBarOpen || this.props.id == "shop-header-search-bar") {
            this.props.onSearch(this.props.searchValue)
        } else {
            this.setState(prevState => {
                return {
                    searchBarOpen: true,
                    buttonText: this.props.searchButtonText
                }
            });
        }

    }

    render() {
        let boundInput = this.UpdateSearchText.bind(this);
        let boundKeyDown = this.KeyDownHandler.bind(this);
        let boundAutocompleteResultClicked = this.AutocompleteResultClicked.bind(this);
        let boundOnBlur = this.HasAutocomplete ? this.onBlurHandler.bind(this) : () => { };
        let boundOnFocus = this.HasAutocomplete ? this.onFocusHandler.bind(this) : () => { };
        let boundSearchClear = this.searchClear.bind(this);
        return (
            <div id={this.props.id} className={this.props.customClasses + " global-search-bar " + (this.state.searchBarOpen ? 'search-open' : 'search-closed')}>
                <div className={"global-search-bar-left"}>
                    {/* Textbox must be uncontrolled to handle keystroke issue in IE */}
                    <svg className={"magnifying-glass " + (this.state.searchBarOpen ? 'search-open' : 'search-closed')}>
                        <use xlinkHref="#info-magnifying-glass"></use>
                    </svg>
                    <Textbox
                        id={this.TextboxId}
                        customClasses={"global-search-bar-textbox"}
                        defaultValue={this.props.defaultValue}
                        onInput={boundInput}
                        onKeyDown={boundKeyDown}
                        onBlur={boundOnBlur}
                        onFocus={boundOnFocus}
                        placeHolder={this.props.placeHolder}
                        testID="search-input"
                    />
                    {(this.props.searchValue.length > 0) &&
                        <div className='search-bar-area-input-clear' onClick={boundSearchClear}>
                            <svg><use xlinkHref="#icon-x-circle"></use></svg>
                        </div>
                    }
                    {(this.props.autocompleteModel) &&
                        <Autocomplete onResultClicked={boundAutocompleteResultClicked}
                            autocompleteResults={this.props.autocompleteModel.hits}
                            searchTerm={this.props.searchValue}
                            activeIndex={this.props.autocompleteModel.activeHit}
                            isActive={this.props.autocompleteModel.isActive}
                            onMouseEnter={this.props.setActiveHit}
                            onMouseLeave={() => this.props.setActiveHit(-1)}
                        />}
                </div>
                <div className={"button-wrapper " + this.ButtonDisplayClass} data-testid="search-button">
                    <a className="global-search-bar-button button-primary search-box-text" onClick={this.handleSearchButtonClick}>{!this.props.setOpen &&
                        <svg className="magnifying-glass">
                            <use xlinkHref="#info-magnifying-glass"></use>
                        </svg>
                    }{this.props.searchBoxText}</a>
                    <a className="global-search-bar-button button-primary search-button-text" onClick={this.handleSearchButtonClick}>{!this.props.setOpen &&
                        <svg className="magnifying-glass">
                            <use xlinkHref="#info-magnifying-glass"></use>
                        </svg>
                    }{this.props.searchButtonText}</a>
                    
                </div>
            </div>
        )
    }
}

const mapStateToProps = (state): IStateProps => ({

})

const mapDispatchToProps = (dispatch): IDispatchProps => {
    return bindActionCreators({
        setActiveHit,
        setAutocompleteHits,
        setAutocompleteActive,
    }, dispatch);
}

export const ConnectedSearchBar = connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(SearchBar);
